import { useEffect, useState } from 'react'
import { ChevronRight, ChevronDown, AlertTriangle, Loader, Check, AlertCircle } from 'lucide-react'
import { useDeviceStore } from '../../store/device-store'

/**
 * Data Logger (Live) settings tab — dynamic, laid out to match the Adobe XD design.
 *
 * Layout: small icon tabs, then a two-column body — left: Device Name + Firmware
 * + action buttons; right: Sample/Log/Transmit with a "More" toggle.
 *
 * Reset / Sync Clock queue real device commands. Firmware "Update" and "Format
 * Memory" are disabled with a reason (device-initiated / unconfirmed command).
 * Sample/Log/Transmit values are displayed (read-only); saving is a follow-up.
 */

const IconTab = ({ active, onClick, title, children }) => (
  <button
    onClick={onClick}
    title={title}
    className={`flex items-center justify-center w-12 h-12 rounded transition-all ${
      active ? 'bg-[#EBF5FB] border border-[#358ECD]' : 'bg-white border border-gray-300 hover:border-[#358ECD]'
    }`}
  >
    <span className={active ? 'text-[#358ECD]' : 'text-gray-500'}>{children}</span>
  </button>
)

const SectionHeading = ({ children, right }) => (
  <div className="flex items-center justify-between border-b border-gray-200 pb-2 mb-3">
    <h3 className="text-sm font-bold text-gray-800">{children}</h3>
    {right}
  </div>
)

const ActionFeedback = ({ status, message }) => {
  if (!status || status === 'idle') return null
  if (status === 'pending') return <Loader className="w-4 h-4 animate-spin text-gray-400" />
  if (status === 'success') return <span className="inline-flex items-center gap-1 text-xs text-green-600"><Check className="w-3.5 h-3.5" />{message || 'Queued'}</span>
  return <span className="inline-flex items-center gap-1 text-xs text-red-600"><AlertCircle className="w-3.5 h-3.5" />{message || 'Failed'}</span>
}

export default function DataLoggerTab({ projectId, deviceId, currentLiveIndex, setCurrentLiveIndex }) {
  const { device, config, isLoading, error, action, loadDevice, updateName, runAction, clearActionStatus } = useDeviceStore()
  const [nameInput, setNameInput] = useState('')
  const [showMore, setShowMore] = useState(false)
  const [expanded, setExpanded] = useState({})

  useEffect(() => {
    if (projectId && deviceId) loadDevice(projectId, deviceId)
  }, [projectId, deviceId, loadDevice])

  const toggle = (id) => setExpanded((e) => ({ ...e, [id]: !e[id] }))

  const onUpdateName = async () => {
    const trimmed = nameInput.trim()
    if (!trimmed) return
    await updateName(projectId, deviceId, trimmed)
    setNameInput('')
    setTimeout(() => clearActionStatus('name'), 4000)
  }

  const onRunAction = async (key, confirmMsg) => {
    if (confirmMsg && !window.confirm(confirmMsg)) return
    await runAction(key, projectId, deviceId)
    setTimeout(() => clearActionStatus(key), 4000)
  }

  if (!deviceId) {
    return <div className="p-6 text-sm text-gray-500">Select a device to view its data logger settings.</div>
  }
  if (isLoading) {
    return <div className="p-6 flex items-center gap-2 text-sm text-gray-500"><Loader className="w-4 h-4 animate-spin" /> Loading device…</div>
  }

  const sensors = config?.sensors || []
  const transmitMin = config?.transmit_interval_min
  const deviceLogMin = sensors.length ? sensors[0].log_interval_min : null
  const NumCell = ({ value }) => (
    <input
      type="number"
      value={value ?? ''}
      readOnly
      className="w-16 px-2 py-1 border border-gray-300 rounded text-xs text-center text-gray-700 bg-white"
    />
  )

  return (
    <div className="w-full h-full flex flex-col bg-white overflow-y-auto px-6 py-5">
      {/* Icon tabs */}
      <div className="flex gap-3 items-center mb-6">
        <IconTab active={currentLiveIndex === 0} onClick={() => setCurrentLiveIndex(0)} title="Data Logger">
          <svg className="w-6 h-6" fill="none" stroke="currentColor" viewBox="0 0 24 24"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth={1.8} d="M5 7a2 2 0 012-2h10a2 2 0 012 2v10a2 2 0 01-2 2H7a2 2 0 01-2-2V7z" /><path strokeLinecap="round" strokeWidth={1.8} d="M8 9h8M8 12h8M8 15h5" /></svg>
        </IconTab>
        <IconTab active={currentLiveIndex === 1} onClick={() => setCurrentLiveIndex(1)} title="Communications">
          <svg className="w-6 h-6" fill="none" stroke="currentColor" viewBox="0 0 24 24"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth={1.8} d="M7 16a4 4 0 01-.88-7.903A5 5 0 1115.9 6L16 6a5 5 0 011 9.9M9 13l3-3m0 0l3 3m-3-3v8" /></svg>
        </IconTab>
        <IconTab active={currentLiveIndex === 2} onClick={() => setCurrentLiveIndex(2)} title="Sensor Readings">
          <svg className="w-6 h-6" fill="none" stroke="currentColor" viewBox="0 0 24 24"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth={1.8} d="M3 12h3l2 6 4-14 2 8h7" /></svg>
        </IconTab>
      </div>

      {error && (
        <div className="mb-4 px-3 py-2 bg-red-50 border border-red-200 rounded text-sm text-red-700 flex items-center gap-2">
          <AlertCircle className="w-4 h-4" /> {error}
        </div>
      )}

      {/* Two-column body */}
      <div className="grid grid-cols-1 lg:grid-cols-2 gap-x-10 gap-y-8">
        {/* LEFT: Device Name + Firmware + Actions */}
        <div className="space-y-8">
          {/* Device Name */}
          <div>
            <SectionHeading>Device Name</SectionHeading>
            <div className="flex items-center gap-3">
              <span className="text-sm text-gray-900 font-medium whitespace-nowrap">{device?.name || '—'}</span>
              <input
                value={nameInput}
                onChange={(e) => setNameInput(e.target.value)}
                maxLength={50}
                placeholder="Change Device Name"
                className="flex-1 text-sm border border-gray-300 rounded px-3 py-1.5 text-gray-700 placeholder-gray-400 focus:outline-none focus:border-[#358ECD]"
              />
              <ActionFeedback status={action.name} message={action.message} />
              <button
                onClick={onUpdateName}
                disabled={action.name === 'pending' || !nameInput.trim()}
                className="px-4 py-1.5 bg-white border border-gray-400 rounded text-sm font-medium text-gray-700 hover:bg-gray-50 transition disabled:opacity-50 disabled:cursor-not-allowed"
              >
                Update
              </button>
            </div>
          </div>

          {/* Firmware */}
          <div>
            <SectionHeading>Firmware</SectionHeading>
            <div>
              {[
                { label: 'BASE Firmware Version', value: device?.firmware?.base },
                { label: 'RTU Firmware Version', value: device?.firmware?.rtu },
              ].map((fw, i) => (
                <div key={fw.label} className={`flex items-center justify-between py-3 ${i === 0 ? 'border-b border-gray-100' : ''}`}>
                  <div className="flex items-center gap-2 min-w-0">
                    <AlertTriangle className="w-4 h-4 text-yellow-500 flex-shrink-0" />
                    <span className="text-sm text-gray-700">{fw.label}</span>
                    <span className="text-sm text-gray-500 ml-2">{fw.value || 'Unknown'}</span>
                  </div>
                  <button
                    disabled
                    title="Firmware updates are initiated by the device over the X2 protocol"
                    className="px-4 py-1.5 bg-white border border-gray-300 rounded text-sm font-medium text-gray-400 cursor-not-allowed"
                  >
                    Update
                  </button>
                </div>
              ))}
            </div>
          </div>

          {/* Action buttons */}
          <div className="flex flex-wrap items-center gap-3">
            <button
              onClick={() => onRunAction('reset', 'Reset this device? It will restart and reload its settings.')}
              disabled={action.reset === 'pending'}
              className="px-5 py-2 border border-gray-400 rounded text-sm font-medium text-gray-700 hover:bg-gray-50 transition disabled:opacity-50"
            >
              Reset Device
            </button>
            <button
              onClick={() => onRunAction('syncClock')}
              disabled={action.syncClock === 'pending'}
              className="px-5 py-2 border border-gray-400 rounded text-sm font-medium text-gray-700 hover:bg-gray-50 transition disabled:opacity-50"
            >
              Sync Clock
            </button>
            <button
              disabled
              title="Format Memory is disabled until the device erase command is confirmed against the firmware spec"
              className="px-5 py-2 border border-gray-300 rounded text-sm font-medium text-gray-400 cursor-not-allowed"
            >
              Format Memory
            </button>
            <ActionFeedback
              status={action.reset !== 'idle' ? action.reset : action.syncClock !== 'idle' ? action.syncClock : null}
              message={action.message}
            />
          </div>
        </div>

        {/* RIGHT: Sample/Log/Transmit */}
        <div>
          <SectionHeading
            right={
              <label className="flex items-center gap-1.5 text-xs text-gray-600 cursor-pointer select-none">
                More
                <input type="checkbox" checked={showMore} onChange={(e) => setShowMore(e.target.checked)} className="w-4 h-4" />
              </label>
            }
          >
            Sample/Log/Transmit
          </SectionHeading>

          <table className="w-full text-xs">
            <thead>
              <tr className="text-gray-600">
                <th className="py-2 text-left font-semibold"></th>
                <th className="py-2 text-center font-semibold w-28">Log <span className="font-normal text-gray-400">(min)</span></th>
                <th className="py-2 text-center font-semibold w-28">Transmit <span className="font-normal text-gray-400">(min)</span></th>
              </tr>
            </thead>
            <tbody>
              {!showMore && (
                <tr className="border-t border-gray-100">
                  <td className="py-2 text-gray-800">{device?.name || '—'}</td>
                  <td className="py-2 text-center"><NumCell value={deviceLogMin} /></td>
                  <td className="py-2 text-center"><NumCell value={transmitMin} /></td>
                </tr>
              )}

              {showMore && sensors.map((sensor) => (
                <SensorRows
                  key={sensor.id}
                  sensor={sensor}
                  transmitMin={transmitMin}
                  expanded={!!expanded[sensor.id]}
                  onToggle={() => toggle(sensor.id)}
                  NumCell={NumCell}
                />
              ))}

              {showMore && sensors.length === 0 && (
                <tr><td colSpan={3} className="py-4 text-center text-gray-400">No sensor configuration available.</td></tr>
              )}
            </tbody>
          </table>
        </div>
      </div>
    </div>
  )
}

function SensorRows({ sensor, transmitMin, expanded, onToggle, NumCell }) {
  const hasParams = (sensor.parameters || []).length > 0
  return (
    <>
      <tr className="border-t border-gray-100 hover:bg-gray-50">
        <td className="py-2">
          <button onClick={onToggle} className="inline-flex items-center gap-1 text-gray-800 font-medium" disabled={!hasParams}>
            {hasParams ? (expanded ? <ChevronDown className="w-3.5 h-3.5" /> : <ChevronRight className="w-3.5 h-3.5" />) : <span className="w-3.5 inline-block" />}
            {sensor.name}
          </button>
        </td>
        <td className="py-2 text-center"><NumCell value={sensor.log_interval_min} /></td>
        <td className="py-2 text-center"><NumCell value={transmitMin} /></td>
      </tr>
      {expanded && (sensor.parameters || []).map((p) => (
        <tr key={p.id} className="border-t border-gray-50 bg-gray-50/40">
          <td className="py-1.5 pl-6 text-gray-600">{p.name}</td>
          <td className="py-1.5 text-center text-gray-400">{sensor.log_interval_min ?? '—'}</td>
          <td className="py-1.5 text-center">
            <span className={`inline-block w-2.5 h-2.5 rounded-full ${p.transmit_flag ? 'bg-green-500' : 'bg-gray-300'}`} title={p.transmit_flag ? 'Transmitting' : 'Not transmitting'} />
          </td>
        </tr>
      ))}
    </>
  )
}
